import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { logger } from '../services/loggingService';
import { dbOperation } from '../utils/database';
import { BlueprintManager } from '../services/blueprintManager';

interface Project {
  id: string;
  name: string;
  description?: string;
  repository_url?: string;
  organization_id?: string;
  created_at: string;
  updated_at: string;
  blueprint_id?: string;
  blueprint_status?: 'draft' | 'locked' | 'executing' | 'complete';
}

interface Blueprint {
  id: string;
  project_id: string;
  content: any;
  version: number;
  is_locked: boolean;
  created_at: string;
  updated_at: string;
}

interface ProjectContextType {
  // Current project
  currentProject: Project | null;
  setCurrentProject: (project: Project | null) => void;
  
  // Current blueprint
  currentBlueprint: Blueprint | null;
  setCurrentBlueprint: (blueprint: Blueprint | null) => void;
  
  // All projects
  projects: Project[];
  loadingProjects: boolean;
  projectsError: string | null;
  
  // Project operations
  loadProjects: () => Promise<void>;
  selectProject: (projectId: string) => Promise<void>;
  createProject: (data: Partial<Project>) => Promise<{ project: Project | null; error?: string }>;
  deleteProject: (projectId: string) => Promise<{ success: boolean; error?: string }>;
  updateProject: (projectId: string, updates: Partial<Project>) => Promise<{ success: boolean; error?: string }>;
  
  // Blueprint operations
  loadBlueprint: (projectId: string) => Promise<void>;
  updateBlueprint: (updates: any) => Promise<{ success: boolean; error?: string }>;
  lockBlueprint: () => Promise<{ success: boolean; error?: string }>;
  
  // Real-time subscriptions
  subscribeToProjectUpdates: (projectId: string) => () => void;
  subscribeToBlueprintUpdates: (blueprintId: string) => () => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export function useProject() {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
}

interface ProjectProviderProps {
  children: ReactNode;
}

export function ProjectProvider({ children }: ProjectProviderProps) {
  const { user } = useAuth();
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [currentBlueprint, setCurrentBlueprint] = useState<Blueprint | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectsError, setProjectsError] = useState<string | null>(null);

  // Load projects when user changes
  useEffect(() => {
    if (user) {
      loadProjects();
    } else {
      setProjects([]);
      setCurrentProject(null);
      setCurrentBlueprint(null);
    }
  }, [user]);

  // Load projects
  const loadProjects = async () => {
    if (!user) return;

    setLoadingProjects(true);
    setProjectsError(null);

    const result = await dbOperation<Project[]>(
      () => supabase
        .from('projects')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false }),
      {
        operation: 'load projects',
        entity: 'projects',
        userId: user.id,
        showUserError: false
      }
    );

    if (result.success) {
      setProjects(result.data || []);
    } else {
      setProjectsError(result.error || 'Failed to load projects');
      logger.error('Failed to load projects', { error: result.error, userId: user.id });
    }

    setLoadingProjects(false);
  };

  // Select a project and load its blueprint
  const selectProject = async (projectId: string) => {
    if (!user) return;

    const project = projects.find(p => p.id === projectId);
    if (project) {
      setCurrentProject(project);
      await loadBlueprint(projectId);
    } else {
      // Load project from database
      const result = await dbOperation<Project>(
        () => supabase
          .from('projects')
          .select('*')
          .eq('id', projectId)
          .eq('user_id', user.id)
          .single(),
        {
          operation: 'load project',
          entity: 'projects',
          userId: user.id,
          projectId,
          showUserError: false
        }
      );

      if (result.success && result.data) {
        setCurrentProject(result.data);
        await loadBlueprint(projectId);
      } else {
        logger.error('Failed to select project', { projectId, error: result.error });
      }
    }
  };

  // Load blueprint for a project
  const loadBlueprint = async (projectId: string) => {
    if (!user) return;

    const { blueprint, error } = await BlueprintManager.getOrCreateBlueprint(
      projectId,
      user.id,
      currentProject?.name,
      currentProject?.description
    );

    if (blueprint) {
      setCurrentBlueprint(blueprint);
    } else {
      logger.error('Failed to load blueprint', { projectId, error });
    }
  };

  // Create a new project
  const createProject = async (data: Partial<Project>) => {
    if (!user) return { project: null, error: 'User not authenticated' };

    const result = await dbOperation<Project>(
      () => supabase
        .from('projects')
        .insert({
          ...data,
          user_id: user.id
        })
        .select()
        .single(),
      {
        operation: 'create project',
        entity: 'projects',
        userId: user.id,
        showUserError: true
      }
    );

    if (result.success && result.data) {
      // Add to projects list
      setProjects(prev => [result.data!, ...prev]);
      
      // Create blueprint for the new project
      const { blueprint } = await BlueprintManager.getOrCreateBlueprint(
        result.data.id,
        user.id,
        result.data.name,
        result.data.description
      );
      
      if (blueprint) {
        logger.info('Blueprint created for new project', { 
          projectId: result.data.id, 
          blueprintId: blueprint.id 
        });
      }
      
      return { project: result.data };
    }

    return { project: null, error: result.error };
  };

  // Delete a project
  const deleteProject = async (projectId: string) => {
    if (!user) return { success: false, error: 'User not authenticated' };

    const result = await dbOperation(
      () => supabase
        .from('projects')
        .delete()
        .eq('id', projectId)
        .eq('user_id', user.id),
      {
        operation: 'delete project',
        entity: 'projects',
        userId: user.id,
        projectId,
        showUserError: true
      }
    );

    if (result.success) {
      // Remove from projects list
      setProjects(prev => prev.filter(p => p.id !== projectId));
      
      // Clear current if deleted
      if (currentProject?.id === projectId) {
        setCurrentProject(null);
        setCurrentBlueprint(null);
      }
      
      return { success: true };
    }

    return { success: false, error: result.error };
  };

  // Update a project
  const updateProject = async (projectId: string, updates: Partial<Project>) => {
    if (!user) return { success: false, error: 'User not authenticated' };

    const result = await dbOperation<Project>(
      () => supabase
        .from('projects')
        .update(updates)
        .eq('id', projectId)
        .eq('user_id', user.id)
        .select()
        .single(),
      {
        operation: 'update project',
        entity: 'projects',
        userId: user.id,
        projectId,
        showUserError: true
      }
    );

    if (result.success && result.data) {
      // Update in projects list
      setProjects(prev => prev.map(p => p.id === projectId ? result.data! : p));
      
      // Update current if selected
      if (currentProject?.id === projectId) {
        setCurrentProject(result.data);
      }
      
      return { success: true };
    }

    return { success: false, error: result.error };
  };

  // Update blueprint
  const updateBlueprint = async (updates: any) => {
    if (!user || !currentBlueprint) {
      return { success: false, error: 'No blueprint selected' };
    }

    const result = await BlueprintManager.updateBlueprint(
      currentBlueprint.id,
      user.id,
      updates,
      false // Don't increment version for normal updates
    );

    if (result.success) {
      // Reload blueprint
      await loadBlueprint(currentBlueprint.project_id);
    }

    return result;
  };

  // Lock blueprint
  const lockBlueprint = async () => {
    if (!user || !currentBlueprint) {
      return { success: false, error: 'No blueprint selected' };
    }

    const result = await BlueprintManager.lockBlueprint(
      currentBlueprint.id,
      user.id
    );

    if (result.success) {
      // Reload blueprint
      await loadBlueprint(currentBlueprint.project_id);
    }

    return result;
  };

  // Subscribe to real-time project updates
  const subscribeToProjectUpdates = (projectId: string) => {
    const subscription = supabase
      .channel(`project-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'projects',
          filter: `id=eq.${projectId}`
        },
        (payload) => {
          logger.debug('Project update received', { projectId, payload });
          
          if (payload.eventType === 'UPDATE' && payload.new) {
            // Update in projects list
            setProjects(prev => prev.map(p => 
              p.id === projectId ? payload.new as Project : p
            ));
            
            // Update current if selected
            if (currentProject?.id === projectId) {
              setCurrentProject(payload.new as Project);
            }
          } else if (payload.eventType === 'DELETE') {
            // Remove from projects list
            setProjects(prev => prev.filter(p => p.id !== projectId));
            
            // Clear current if deleted
            if (currentProject?.id === projectId) {
              setCurrentProject(null);
              setCurrentBlueprint(null);
            }
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  };

  // Subscribe to real-time blueprint updates
  const subscribeToBlueprintUpdates = (blueprintId: string) => {
    const subscription = supabase
      .channel(`blueprint-${blueprintId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_blueprints',
          filter: `id=eq.${blueprintId}`
        },
        (payload) => {
          logger.debug('Blueprint update received', { blueprintId, payload });
          
          if (payload.eventType === 'UPDATE' && payload.new) {
            // Update current blueprint if it's the one being updated
            if (currentBlueprint?.id === blueprintId) {
              setCurrentBlueprint(payload.new as Blueprint);
            }
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  };

  const value: ProjectContextType = {
    currentProject,
    setCurrentProject,
    currentBlueprint,
    setCurrentBlueprint,
    projects,
    loadingProjects,
    projectsError,
    loadProjects,
    selectProject,
    createProject,
    deleteProject,
    updateProject,
    loadBlueprint,
    updateBlueprint,
    lockBlueprint,
    subscribeToProjectUpdates,
    subscribeToBlueprintUpdates,
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
}
